import React, { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Formik, FormikHelpers as FormikActions, Field, FieldProps } from 'formik';
import styled from 'styled-components';
import { Button } from '@inplayer-org/inplayer-ui';

// hooks
import useSharedState from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedState';

// types
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// actions
import { openModal, closeModal } from 'ducks/merchantDucks/modal/actions';
import { updateItemTemplateId } from 'ducks/merchantDucks/items/actions';

// selectors
import { getBranding } from 'ducks/merchantDucks/branding/selectors';

// components
import { FullHeightForm, ContentContainer, CenterContentContainer } from 'components/SharedStyledElements';
import AccordionFooter from 'components/AccordionFooter';
import { Note } from 'pages/MerchantPages/Settings/Auth/Oauth/containers/Oauth';
import PreviewTemplateBox from 'pages/MerchantPages/Assets/PreviewTemplates/PreviewTemplateBox';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';
import ConfirmModal from 'components/Modals/ConfirmModal';

const NoMarginNote = styled(Note)`
  margin-top: 0;
`;

const ButtonWrapper = styled.div`
  text-align: right;
`;

interface FormValues {
  templateId: number;
}

interface OwnProps {
  closePanel: () => void;
  handlePreviewChange: (preview: number) => void;
  itemType: string;
  savedTemplateId: number;
  accordionLabel: string;
}

type Props = OwnProps;

const PreviewTemplates: FunctionComponent<Props> = ({
  closePanel,
  handlePreviewChange,
  itemType,
  savedTemplateId,
  accordionLabel,
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isTemplate = pathname.includes('templates');

  const [, , resetTemplateId] = useSharedState<number | null>('previewTemplateId', null);

  const {
    defaultBrandingSet: { default: hasDefaultBranding, template_id: defaultBrandingId },
  } = useSelector(getBranding);

  const onFormSubmit = async ({ templateId }: FormValues, { setSubmitting }: FormikActions<FormValues>) => {
    await dispatch(updateItemTemplateId({ templateId, itemType, isTemplate }));
    setSubmitting(false);
    closePanel();
  };

  const closePanelHandler = (resetForm: () => void) => () => {
    resetForm();
    resetTemplateId(savedTemplateId);
    closePanel();
  };

  const handleOpenResetModal = () => {
    dispatch(openModal(ModalNames.ResetPreviewTemplate));
  };

  const handleReset = async () => {
    await dispatch(updateItemTemplateId({ templateId: null, itemType, isTemplate }));
    dispatch(closeModal());
  };

  const initialValues = { templateId: savedTemplateId || defaultBrandingId };

  return (
    <>
      <Formik onSubmit={onFormSubmit} initialValues={initialValues} enableReinitialize>
        {({ isSubmitting, dirty, resetForm }) => (
          <FullHeightForm>
            <FormAccordionHelper
              initialValues={initialValues}
              index={SectionType.Templates}
              closePanel={closePanelHandler(resetForm)}
            />
            <ContentContainer>
              <CenterContentContainer columns={1} rowGap="1.5rem">
                {hasDefaultBranding && !savedTemplateId && (
                  <NoMarginNote variant="p">Default branding preview in use.</NoMarginNote>
                )}
                {!!savedTemplateId && (
                  <ButtonWrapper>
                    <Button type="button" size="sm" onClick={handleOpenResetModal}>
                      Reset to default
                    </Button>
                  </ButtonWrapper>
                )}
                <Field name="templateId">
                  {({ field: { name, value }, form: { setFieldValue } }: FieldProps<any>) => {
                    const handlePreviewClick = (id: number) => {
                      setFieldValue(name, id);
                      handlePreviewChange(id);
                    };

                    return <PreviewTemplateBox handlePreviewChange={handlePreviewClick} previewTemplateId={value} />;
                  }}
                </Field>
              </CenterContentContainer>
              <AccordionFooter
                accordionLabel={accordionLabel}
                handleCancel={closePanelHandler(resetForm)}
                disabled={isSubmitting || !dirty}
              />
            </ContentContainer>
          </FullHeightForm>
        )}
      </Formik>
      <ConfirmModal
        modalName={ModalNames.ResetPreviewTemplate}
        message={`Are you sure you want to reset to default ${hasDefaultBranding ? 'branding' : ''} preview?`}
        handleConfirm={handleReset}
        buttonText="Reset"
      />
    </>
  );
};

export default PreviewTemplates;
